import type { Difficulty } from './types';

export type SoundEffect = 'move' | 'capture' | 'check' | 'win' | 'loss' | 'draw';
export type SpeechFrequency = 'key' | 'all';
export type VoiceCue = 'opening' | 'reply' | 'capture' | 'check' | 'threatened' | 'hurt' | 'win' | 'loss' | 'draw';
export type MusicTheme = 'selection' | 'duel' | 'result';

const RECORDED_VOICE_URLS = import.meta.glob<string>('./assets/voices/*/*.mp3', {
  eager: true, query: '?url', import: 'default',
});
const NARRATION_URLS = import.meta.glob<string>('./assets/narration/*.mp3', {
  eager: true, query: '?url', import: 'default',
});
const MUSIC_URLS = import.meta.glob<string>('./assets/music/*.mp3', {
  eager: true, query: '?url', import: 'default',
});

function recordedVoiceUrl(difficulty: Difficulty, cue: VoiceCue): string | undefined {
  return RECORDED_VOICE_URLS[`./assets/voices/${difficulty}/${cue}.mp3`];
}

function narrationUrl(difficulty: Difficulty): string | undefined {
  return NARRATION_URLS[`./assets/narration/${difficulty}.mp3`];
}

function musicUrls(theme: MusicTheme): string[] {
  const pattern = new RegExp(`/music/${theme}(?:-\\d+)?\\.mp3$`);
  return Object.entries(MUSIC_URLS)
    .filter(([path]) => pattern.test(path))
    .sort(([left], [right]) => left.localeCompare(right, undefined, { numeric: true }))
    .map(([, url]) => url);
}

export function pickNextTrackIndex(trackCount: number, previousIndex: number, random = Math.random): number {
  if (trackCount <= 1) return 0;
  const roll = Math.max(0, Math.min(0.999999, random()));
  if (previousIndex < 0 || previousIndex >= trackCount) return Math.floor(roll * trackCount);
  const candidate = Math.min(trackCount - 2, Math.floor(roll * (trackCount - 1)));
  return candidate >= previousIndex ? candidate + 1 : candidate;
}

export interface AudioSettings {
  effects: boolean;
  speech: boolean;
  speechFrequency: SpeechFrequency;
  music: boolean;
  musicVolume: number;
}

export interface VoiceStyle {
  pitch: number;
  rate: number;
  volume: number;
  voiceSlot: number;
}

const SETTINGS_KEY = 'cloud-xiangqi:audio:v1';
const DEFAULT_SETTINGS: AudioSettings = { effects: true, speech: true, speechFrequency: 'key', music: true, musicVolume: 0.22 };

export const CHARACTER_VOICE_STYLES: Record<Difficulty, VoiceStyle> = {
  beginner: { pitch: 1.28, rate: 1.03, volume: 0.86, voiceSlot: 0 },
  trader: { pitch: 0.86, rate: 0.91, volume: 0.92, voiceSlot: 1 },
  cannonhorse: { pitch: 1.08, rate: 1.17, volume: 0.92, voiceSlot: 2 },
  gambiteer: { pitch: 0.76, rate: 1.13, volume: 0.98, voiceSlot: 8 },
  attacker: { pitch: 0.79, rate: 1.08, volume: 1, voiceSlot: 3 },
  defender: { pitch: 0.72, rate: 0.82, volume: 0.94, voiceSlot: 4 },
  pawn: { pitch: 1.12, rate: 0.94, volume: 0.9, voiceSlot: 9 },
  miser: { pitch: 0.94, rate: 0.76, volume: 0.9, voiceSlot: 5 },
  blockader: { pitch: 0.62, rate: 0.74, volume: 0.95, voiceSlot: 10 },
  scholar: { pitch: 0.92, rate: 0.86, volume: 0.9, voiceSlot: 11 },
  veteran: { pitch: 0.66, rate: 0.7, volume: 0.9, voiceSlot: 6 },
  master: { pitch: 0.84, rate: 0.8, volume: 0.94, voiceSlot: 7 },
};

export function shouldSpeakEvent(settings: AudioSettings, important: boolean): boolean {
  return settings.speech && (important || settings.speechFrequency === 'all');
}

function loadSettings(): AudioSettings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') as Partial<AudioSettings>;
    return {
      effects: typeof stored.effects === 'boolean' ? stored.effects : DEFAULT_SETTINGS.effects,
      speech: typeof stored.speech === 'boolean' ? stored.speech : DEFAULT_SETTINGS.speech,
      speechFrequency: stored.speechFrequency === 'all' ? 'all' : 'key',
      music: typeof stored.music === 'boolean' ? stored.music : DEFAULT_SETTINGS.music,
      musicVolume: typeof stored.musicVolume === 'number' ? Math.max(0, Math.min(0.5, stored.musicVolume)) : DEFAULT_SETTINGS.musicVolume,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveSettings(settings: AudioSettings): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export class GameAudio {
  private settings = loadSettings();
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private recordedSpeech: HTMLAudioElement | null = null;
  private music: HTMLAudioElement | null = null;
  private musicTheme: MusicTheme = 'selection';
  private previousMusicIndex: Record<MusicTheme, number> = { selection: -1, duel: -1, result: -1 };
  private unlocked = false;
  private musicDucked = false;

  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  updateSettings(next: Partial<AudioSettings>): AudioSettings {
    this.settings = { ...this.settings, ...next };
    saveSettings(this.settings);
    if (!this.settings.speech) this.stopSpeech();
    if (!this.settings.music) this.stopMusic();
    else {
      this.syncMusicVolume();
      if (this.unlocked) void this.playMusic();
    }
    return this.getSettings();
  }

  speechAvailable(): boolean {
    return Object.keys(RECORDED_VOICE_URLS).length > 0
      || (typeof speechSynthesis !== 'undefined' && typeof SpeechSynthesisUtterance !== 'undefined');
  }

  effectsAvailable(): boolean {
    return typeof AudioContext !== 'undefined';
  }

  narrationAvailable(): boolean {
    return Object.keys(NARRATION_URLS).length > 0;
  }

  musicAvailable(): boolean {
    return Object.keys(MUSIC_URLS).length > 0 && typeof Audio !== 'undefined';
  }

  async unlock(): Promise<void> {
    this.unlocked = true;
    if (this.effectsAvailable()) {
      this.context ??= new AudioContext();
      if (!this.master) {
        this.master = this.context.createGain();
        this.master.connect(this.context.destination);
      }
      this.master.gain.value = 0.52;
      if (this.context.state === 'suspended') await this.context.resume();
    }
    if (this.settings.music) await this.playMusic();
  }

  setMusicTheme(theme: MusicTheme, reshuffle = false): void {
    if (!reshuffle && theme === this.musicTheme && this.music) {
      if (this.unlocked && this.settings.music && this.music.paused) void this.music.play().catch(() => undefined);
      return;
    }
    this.musicTheme = theme;
    this.stopMusic();
    if (this.unlocked && this.settings.music) void this.playMusic();
  }

  private async playMusic(): Promise<void> {
    if (!this.settings.music || !this.musicAvailable()) return;
    const tracks = musicUrls(this.musicTheme);
    const trackIndex = pickNextTrackIndex(tracks.length, this.previousMusicIndex[this.musicTheme]);
    const url = tracks[trackIndex];
    if (!url) return;
    if (!this.music || this.music.dataset.track !== url) {
      this.stopMusic();
      this.music = new Audio(url);
      this.music.dataset.theme = this.musicTheme;
      this.music.dataset.track = url;
      this.music.preload = 'auto';
      this.music.loop = true;
      this.previousMusicIndex[this.musicTheme] = trackIndex;
    }
    this.syncMusicVolume();
    await this.music.play().catch(() => undefined);
  }

  private stopMusic(): void {
    if (!this.music) return;
    this.music.pause();
    this.music.currentTime = 0;
    this.music = null;
  }

  private syncMusicVolume(): void {
    if (this.music) this.music.volume = this.settings.musicVolume * (this.musicDucked ? 0.32 : 1);
  }

  private duckMusic(ducked: boolean): void {
    this.musicDucked = ducked;
    this.syncMusicVolume();
  }

  private tone(frequency: number, start: number, duration: number, gain: number, type: OscillatorType = 'sine', endFrequency?: number): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master) return;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
    envelope.gain.setValueAtTime(Math.max(0.0001, gain), start);
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(envelope).connect(master);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  private knock(start: number, strong = false): void {
    const base = strong ? 172 : 205;
    this.tone(base, start, strong ? 0.13 : 0.085, strong ? 0.72 : 0.48, 'triangle', base * 0.54);
    this.tone(base * 2.15, start + 0.004, strong ? 0.07 : 0.045, strong ? 0.22 : 0.14, 'sine', base * 1.4);
  }

  async play(effect: SoundEffect): Promise<void> {
    if (!this.settings.effects) return;
    await this.unlock();
    const context = this.context;
    if (!context || context.state !== 'running') return;
    const now = context.currentTime + 0.008;
    if (effect === 'move') this.knock(now);
    else if (effect === 'capture') {
      this.knock(now, true);
      this.knock(now + 0.075, false);
    } else if (effect === 'check') {
      this.knock(now, true);
      this.tone(392, now + 0.04, 0.38, 0.25, 'sine', 220);
      this.tone(784, now + 0.045, 0.17, 0.1, 'sine', 440);
    } else if (effect === 'win') {
      [392, 494, 659].forEach((frequency, index) => this.tone(frequency, now + index * 0.11, 0.42, 0.22, 'sine'));
    } else if (effect === 'loss') {
      [330, 277, 196].forEach((frequency, index) => this.tone(frequency, now + index * 0.13, 0.48, 0.2, 'triangle'));
    } else {
      this.tone(294, now, 0.36, 0.18, 'sine');
      this.tone(294, now + 0.18, 0.4, 0.14, 'sine');
    }
  }

  private speakWithDeviceVoice(difficulty: Difficulty, text: string): void {
    if (typeof speechSynthesis === 'undefined' || typeof SpeechSynthesisUtterance === 'undefined') return;
    const synthesis = speechSynthesis;
    const style = CHARACTER_VOICE_STYLES[difficulty];
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = synthesis.getVoices();
    const chineseVoices = voices.filter((voice) => /^zh(?:-|_)/i.test(voice.lang));
    const pool = chineseVoices.length ? chineseVoices : voices;
    if (pool.length) utterance.voice = pool[style.voiceSlot % pool.length] ?? null;
    utterance.lang = utterance.voice?.lang || 'zh-TW';
    utterance.pitch = style.pitch;
    utterance.rate = style.rate;
    utterance.volume = style.volume;
    synthesis.cancel();
    this.duckMusic(true);
    utterance.addEventListener('end', () => this.duckMusic(false), { once: true });
    utterance.addEventListener('error', () => this.duckMusic(false), { once: true });
    synthesis.speak(utterance);
  }

  private playRecordedSpeech(url: string, volume: number, fallback: () => void): void {
    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.volume = volume;
    this.recordedSpeech = audio;
    this.duckMusic(true);
    const finish = () => {
      if (this.recordedSpeech === audio) this.recordedSpeech = null;
      this.duckMusic(false);
    };
    audio.addEventListener('ended', finish, { once: true });
    void audio.play().catch(() => {
      finish();
      fallback();
    });
  }

  speak(difficulty: Difficulty, cue: VoiceCue, text: string, important = true): void {
    if (!shouldSpeakEvent(this.settings, important) || !this.speechAvailable() || !text.trim()) return;
    this.stopSpeech();
    const url = recordedVoiceUrl(difficulty, cue);
    if (url && typeof Audio !== 'undefined') {
      this.playRecordedSpeech(url, CHARACTER_VOICE_STYLES[difficulty].volume, () => this.speakWithDeviceVoice(difficulty, text));
      return;
    }
    this.speakWithDeviceVoice(difficulty, text);
  }

  narrate(difficulty: Difficulty, text: string): void {
    if (!this.settings.speech || !text.trim()) return;
    this.stopSpeech();
    const url = narrationUrl(difficulty);
    if (url && typeof Audio !== 'undefined') {
      this.playRecordedSpeech(url, 0.92, () => this.speakWithDeviceVoice('master', text));
      return;
    }
    this.speakWithDeviceVoice('master', text);
  }

  stopSpeech(): void {
    if (this.recordedSpeech) {
      this.recordedSpeech.pause();
      this.recordedSpeech.currentTime = 0;
      this.recordedSpeech = null;
    }
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    this.duckMusic(false);
  }
}

export const gameAudio = new GameAudio();
